import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * NR-6: connectivity status UI for the network-resilience feature.
 *
 * Pure presentational component driven by the NR-5a/5b connection status +
 * measured-quality reason. Renders:
 *  - ACTIVE + stable → a brief green "Connection stable" pill that AUTO-HIDES
 *    after ~3s (only the stable indicator auto-hides);
 *  - DEGRADED (unstable) → amber "unstable connection" banner (persistent);
 *  - DEGRADED (poor)     → orange "poor connection" banner (persistent);
 *  - RECONNECTING        → blocking modal with the reconnect-window countdown;
 *  - INTERRUPTED         → blocking "rescheduled" modal.
 *
 * Rendered inside the realtime provider; with the feature disabled the status
 * stays ACTIVE and the stable pill simply auto-hides (dark by default).
 */
import React, { useEffect, useRef, useState } from 'react';
const STABLE_INDICATOR_MS = 3000; // "stable" confirmation shows briefly, then hides
// WS-1: once a degraded pill is shown, keep it visible for at least this long so
// it can't flash in and then immediately out on a brief fluctuation (the monitor
// already smooths the underlying quality; this guards the render layer too).
const DEGRADED_MIN_DISPLAY_MS = Number(process.env.REACT_APP_NETQ_PILL_MIN_DISPLAY_MS) || 5000;
function formatCountdown(seconds) {
    if (seconds == null)
        return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}
const overlay = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
};
const card = {
    background: '#fff',
    borderRadius: 12,
    padding: '28px 32px',
    maxWidth: 460,
    textAlign: 'center',
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};
// Centered, rounded "toast" pill at the top — the usual connection-status style.
const pill = (bg, fg) => ({
    position: 'fixed',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    background: bg,
    color: fg,
    padding: '10px 20px',
    borderRadius: 999,
    zIndex: 9999,
    fontSize: 14,
    fontWeight: 500,
    maxWidth: '92%',
    textAlign: 'center',
    boxShadow: '0 4px 18px rgba(0,0,0,0.20)',
    whiteSpace: 'nowrap',
});
export const ConnectivityOverlay = ({ status, quality, reconnectCountdown, reconnectError, }) => {
    // "Stable" indicator: show briefly whenever we (re)enter ACTIVE, then auto-hide.
    const [showStable, setShowStable] = useState(false);
    useEffect(() => {
        if (status !== 'ACTIVE') {
            setShowStable(false);
            return;
        }
        setShowStable(true);
        const t = setTimeout(() => setShowStable(false), STABLE_INDICATOR_MS);
        return () => clearTimeout(t);
    }, [status]);
    // WS-1: sticky degraded pill with a minimum display time. While DEGRADED we
    // mirror the live quality; when the status leaves DEGRADED for ACTIVE we keep
    // the last degraded pill until DEGRADED_MIN_DISPLAY_MS has elapsed, so a brief
    // dip can't produce a sub-second flash. Blocking states (RECONNECTING /
    // INTERRUPTED) own the screen, so we drop the sticky pill immediately there.
    const [stickyDegraded, setStickyDegraded] = useState(null);
    const degradedShownAtRef = useRef(0);
    useEffect(() => {
        if (status === 'DEGRADED' && quality) {
            setStickyDegraded(quality);
            degradedShownAtRef.current = Date.now();
            return;
        }
        if (status !== 'ACTIVE') {
            setStickyDegraded(null);
            return;
        }
        if (!stickyDegraded)
            return;
        const remaining = DEGRADED_MIN_DISPLAY_MS - (Date.now() - degradedShownAtRef.current);
        if (remaining <= 0) {
            setStickyDegraded(null);
            return;
        }
        const t = setTimeout(() => setStickyDegraded(null), remaining);
        return () => clearTimeout(t);
    }, [status, quality, stickyDegraded]);
    // Recovery is automatic (auto-reconnect retries for the whole reconnect
    // window) — show a passive status line instead of a manual Rejoin button.
    const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
    useEffect(() => {
        const onUp = () => setIsOnline(true);
        const onDown = () => setIsOnline(false);
        window.addEventListener('online', onUp);
        window.addEventListener('offline', onDown);
        return () => {
            window.removeEventListener('online', onUp);
            window.removeEventListener('offline', onDown);
        };
    }, []);
    const rejoinSection = (_jsx("p", { style: { margin: '14px 0 0', fontSize: 13, color: '#777' }, children: isOnline ? 'Reconnecting you automatically…' : 'Waiting for your internet to return…' }));
    // A recoverable error (e.g. the OpenAI session taking a while to come back)
    // takes priority over the plain countdown — shown as an informational
    // "Reconnecting…" card; recovery proceeds automatically.
    if (reconnectError) {
        return (_jsx("div", { role: 'dialog', "aria-label": 'Reconnect', style: overlay, children: _jsxs("div", { style: card, children: [_jsx("h3", { style: { margin: '0 0 8px' }, children: "Reconnecting\u2026" }), _jsx("p", { style: { margin: 0, color: '#444' }, children: reconnectError }), rejoinSection] }) }));
    }
    if (status === 'RECONNECTING') {
        return (_jsx("div", { role: 'dialog', "aria-label": 'Connection lost', style: overlay, children: _jsxs("div", { style: card, children: [_jsx("h3", { style: { margin: '0 0 8px' }, children: "Connection lost" }), _jsx("p", { style: { margin: '0 0 12px', color: '#444' }, children: "We've paused your interview and your time is safe. Reconnecting\u2026" }), _jsx("div", { style: { fontSize: 28, fontWeight: 700 }, children: formatCountdown(reconnectCountdown) }), _jsx("p", { style: { margin: '8px 0 0', fontSize: 13, color: '#777' }, children: "time remaining to reconnect" }), rejoinSection] }) }));
    }
    if (status === 'INTERRUPTED') {
        return (_jsx("div", { role: 'dialog', "aria-label": 'Interview interrupted', style: overlay, children: _jsxs("div", { style: card, children: [_jsx("h3", { style: { margin: '0 0 8px' }, children: "We couldn't restore your connection" }), _jsx("p", { style: { margin: 0, color: '#444' }, children: "Don't worry \u2014 this was a technical issue, not a reflection of your performance. We've scheduled a fresh interview for you. Please check your email for the new link." })] }) }));
    }
    // Show the degraded pill while DEGRADED, or briefly after recovery while the
    // sticky min-display window holds (effectiveQuality carries the last value).
    const effectiveQuality = status === 'DEGRADED' ? quality : status === 'ACTIVE' ? stickyDegraded : null;
    if (effectiveQuality) {
        return effectiveQuality === 'poor' ? (_jsx("div", { role: 'status', style: pill('#FFEDD5', '#9A3412'), children: "\u26A0 Connection is slow \u2014 responses may lag briefly." })) : (_jsx("div", { role: 'status', style: pill('#FEF3C7', '#92400E'), children: "\u26A0 Your connection looks a little unstable." }));
    }
    // ACTIVE — brief "stable" confirmation, then auto-hides
    return showStable ? (_jsx("div", { role: 'status', style: pill('#DCFCE7', '#166534'), children: "\u25CF Connection stable" })) : null;
};
